import { useState, useCallback, useRef } from 'react';
import type { PageContext } from '@/lib/page-context';
import { guardOutageCode, GuardOutageError, type GuardOutageCode } from '@/lib/aiGuardOutage';

const API_URL = import.meta.env.VITE_API_URL || '/api';

function getToken(): string | null {
  if (typeof window !== 'undefined') return localStorage.getItem('authToken');
  return null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool_call';
  content: string;
  toolCalls?: { name: string; args: Record<string, any>; result?: Record<string, any> }[];
  feedback?: 'helpful' | 'not_helpful';
  createdAt: string;
  /**
   * Set on the local notice that replaces a reply when the safety check gave
   * no verdict in time. Never persisted: the server stored nothing.
   */
  guardOutage?: {
    code: GuardOutageCode;
    /** The user message that was refused, and what to send again. */
    userMessageId: string;
    content: string;
  };
}

export interface AIConversation {
  id: string;
  title: string;
  model: string;
  page_context: any;
  is_archived: boolean;
  message_count?: number;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UserTier = 'free' | 'pro' | 'team';

export function useAIAssistant(pageContext: PageContext | null) {
  // Tier
  const [userTier, setUserTier] = useState<UserTier>('free');

  // Conversation list
  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Messages
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeToolCalls, setActiveToolCalls] = useState<{ name: string; args: Record<string, any> }[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  // Free limit hit
  const [hitFreeLimit, setHitFreeLimit] = useState(false);

  // Navigation action from AI
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // ── API helper ──
  const apiFetch = useCallback(async (path: string, opts?: RequestInit) => {
    const token = getToken();
    if (!token) return null;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...((opts?.headers as Record<string, string>) || {}),
    };
    return fetch(`${API_URL}${path}`, { ...opts, headers });
  }, []);

  // ── Load conversation list ──
  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const res = await apiFetch('/assistant/conversations');
      if (res?.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
        if (data.tier) setUserTier(data.tier as UserTier);
      }
    } catch (e) {
      console.error('[useAIAssistant] load conversations:', e);
    } finally {
      setLoadingConversations(false);
    }
  }, [apiFetch]);

  // ── Create new conversation ──
  const createConversation = useCallback(async (title?: string): Promise<string | null> => {
    try {
      const res = await apiFetch('/assistant/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title || 'New chat',
          page_context: pageContext || undefined,
        }),
      });
      if (res?.ok) {
        const data = await res.json();
        const conv = data.conversation;
        setConversations(prev => [conv, ...prev]);
        setActiveConversationId(conv.id);
        setMessages([]);
        if (data.tier) setUserTier(data.tier as UserTier);
        return conv.id;
      }
    } catch (e) {
      console.error('[useAIAssistant] create conversation:', e);
    }
    return null;
  }, [apiFetch, pageContext]);

  // ── Open existing conversation ──
  const openConversation = useCallback(async (convId: string) => {
    setActiveConversationId(convId);
    setLoadingMessages(true);
    setMessages([]);
    try {
      const res = await apiFetch(`/assistant/conversations/${convId}`);
      if (res?.ok) {
        const data = await res.json();
        const msgs: AIMessage[] = (data.messages || []).map((m: any) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          toolCalls: m.tool_calls ? (typeof m.tool_calls === 'string' ? JSON.parse(m.tool_calls) : m.tool_calls) : undefined,
          feedback: m.feedback || undefined,
          createdAt: m.created_at,
        }));
        setMessages(msgs);
      }
    } catch (e) {
      console.error('[useAIAssistant] open conversation:', e);
    } finally {
      setLoadingMessages(false);
    }
  }, [apiFetch]);

  // ── Send message with SSE streaming ──
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isStreaming) return;

    let convId = activeConversationId;

    // Auto-create conversation if none active
    if (!convId) {
      convId = await createConversation(content.slice(0, 50));
      if (!convId) return;
    }

    // Add user message to UI immediately
    const userMsg: AIMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);
    setStreamingText('');
    setActiveToolCalls([]);
    setPendingNavigation(null);

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/assistant/conversations/${convId}/stream`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content.trim(),
          page_context: pageContext || undefined,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.code === 'FREE_LIMIT') {
          throw new Error('__FREE_LIMIT__');
        }
        const outage = guardOutageCode(errData);
        if (outage) throw new GuardOutageError(outage);
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === 'token' && data.text) {
                fullText += data.text;
                setStreamingText(fullText);
              } else if (currentEvent === 'tool_call') {
                setActiveToolCalls(prev => [...prev, { name: data.name, args: data.args }]);
                // Broadcast so whichever surface owns the action can run it.
                // The stream carries the tool NAME and ARGS but not the tool's
                // return value, so consumers act on those directly — that is
                // enough for every client-executed tool we have.
                //
                // A window event rather than context/prop-drilling: the
                // assistant is mounted once in DashboardLayout while the
                // handlers live in whatever page is open. Same pattern as the
                // existing `soltik:compose` event.
                try {
                  window.dispatchEvent(new CustomEvent('soltik:ai-tool-call', {
                    detail: { name: data.name, args: data.args || {} },
                  }));
                } catch { /* non-browser env */ }
              } else if (currentEvent === 'done') {
                // Stream complete
              } else if (currentEvent === 'error') {
                throw new Error(data.message || 'Stream error');
              }
            } catch (parseErr) {
              // Ignore parse errors on partial data
              if (currentEvent === 'error') throw parseErr;
            }
            currentEvent = '';
          }
        }
      }

      // Add the complete assistant message
      if (fullText) {
        const assistantMsg: AIMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: fullText,
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMsg]);

        // Client-executed tools (navigate_to, the Studio canvas tools) are
        // dispatched as `soltik:ai-tool-call` when the tool_call event
        // arrives above, not inferred from the assistant's prose.
      }

      // Update conversation title if it's the first message
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, updated_at: new Date().toISOString() } : c
      ));

    } catch (err: any) {
      if (err.name === 'AbortError') return;

      if (err.message === '__FREE_LIMIT__') {
        setHitFreeLimit(true);
        // Remove the optimistic user message
        setMessages(prev => prev.filter(m => m.id !== userMsg.id));
        return;
      }

      if (err instanceof GuardOutageError) {
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          guardOutage: { code: err.code, userMessageId: userMsg.id, content: userMsg.content },
        }]);
        return;
      }

      console.error('[useAIAssistant] stream error:', err);

      const errorMsg: AIMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, something went wrong: ${err.message}`,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsStreaming(false);
      setStreamingText('');
      setActiveToolCalls([]);
      abortRef.current = null;
    }
  }, [activeConversationId, isStreaming, createConversation, pageContext]);

  // ── Resend a message the safety check timed out on ──
  // Drops the refused message and its notice, then sends the same text again,
  // so the thread reads as if the first attempt had gone through.
  const retryGuardOutage = useCallback((noticeId: string) => {
    const outage = messages.find(m => m.id === noticeId)?.guardOutage;
    if (!outage || isStreaming) return;
    setMessages(prev => prev.filter(m => m.id !== noticeId && m.id !== outage.userMessageId));
    void sendMessage(outage.content);
  }, [messages, isStreaming, sendMessage]);

  // ── Stop streaming ──
  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // ── Delete conversation ──
  const deleteConversation = useCallback(async (convId: string) => {
    try {
      const res = await apiFetch(`/assistant/conversations/${convId}`, { method: 'DELETE' });
      if (res?.ok) {
        setConversations(prev => prev.filter(c => c.id !== convId));
        if (activeConversationId === convId) {
          setActiveConversationId(null);
          setMessages([]);
        }
      }
    } catch (e) {
      console.error('[useAIAssistant] delete conversation:', e);
    }
  }, [apiFetch, activeConversationId]);

  // ── Submit feedback ──
  const submitFeedback = useCallback(async (messageId: string, feedback: 'helpful' | 'not_helpful') => {
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, feedback } : m));
    try {
      await apiFetch('/assistant/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: messageId, feedback }),
      });
    } catch (e) {
      console.error('[useAIAssistant] feedback:', e);
    }
  }, [apiFetch]);

  // ── Back to conversation list ──
  const backToList = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setStreamingText('');
  }, []);

  return {
    // Tier
    userTier,
    isPro: userTier === 'pro' || userTier === 'team',
    hitFreeLimit,

    // Conversations
    conversations,
    activeConversationId,
    loadingConversations,
    loadConversations,
    createConversation,
    openConversation,
    deleteConversation,
    backToList,

    // Messages
    messages,
    loadingMessages,

    // Streaming
    isStreaming,
    streamingText,
    activeToolCalls,
    sendMessage,
    retryGuardOutage,
    stopStreaming,

    // Feedback
    submitFeedback,

    // Navigation
    pendingNavigation,
    clearNavigation: () => setPendingNavigation(null),
  };
}
